import { createTheme } from "@mui/material"
import React from "react"

declare module "@mui/material/styles" {
    interface Palette {
        status: {
            error: string
            warning: string
            success: string
            disabled: string
            info: string
        }
        waiting: {
            main: string
        }
        disabled: {
            main: string
        }
        settings: {
            main: string
            hover: string
        }
        backgroundSucces: {
            default: string
        }
        darkgrey: {
            main: string
        }
        lightgrey: {
            main: string
        }
        selectableChip: {
            bgSelected: string
            bgUnselected: string
            textSelected: string
            textUnselected: string
            bgUnselectedHover: string
        }
        oreveChipButton: {
            bgUnselected: string
            bgUnselectedHover: string
            bgSelected: string
            bgSelectedHover: string
            textSelected: string
            textUnselected: string
        }
        textfield: {
            default: string
            disabled: string
        }
    }

    // allow configuration using `createTheme`
    interface PaletteOptions {
        status?: {
            error?: string
            warning?: string
            success?: string
            disabled?: string
            info?: string
        }
        waiting: {
            main?: string
        }
        disabled: {
            main: string
        }
        settings: {
            main?: string
            hover?: string
        }
        backgroundSucces: {
            default?: string
        }
        darkgrey: {
            main?: string
        }
        lightgrey: {
            main?: string
        }
        selectableChip: {
            bgSelected: string
            bgUnselected: string
            textSelected: string
            textUnselected: string
            bgUnselectedHover: string
        }
        oreveChipButton: {
            bgUnselected: string
            bgUnselectedHover: string
            bgSelected: string
            bgSelectedHover: string
            textSelected: string
            textUnselected: string
        }
        textfield: {
            default: string
            disabled: string
        }
        selectableButtonGroup: {
            highlight: string
            background: string
            textSelected: string
            textUnselected: string
            bgUnselectedHover: string
            highlightHover: string

        }
    }

    interface PaletteColor {
        hover?: string
    }

    interface SimplePaletteColorOptions {
        hover?: string
    }

    interface TypographyVariants {
        displayLarge: React.CSSProperties
        display: React.CSSProperties
        displaySmall: React.CSSProperties
        headlineLarge: React.CSSProperties
        headline: React.CSSProperties
        headlineSmall: React.CSSProperties
        titleLarge: React.CSSProperties
        title: React.CSSProperties
        titleSmall: React.CSSProperties
        labelLarge: React.CSSProperties
        label: React.CSSProperties
        labelSmall: React.CSSProperties
        bodyLarge: React.CSSProperties
        body: React.CSSProperties
        bodySmall: React.CSSProperties
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        displayLarge?: React.CSSProperties
        display?: React.CSSProperties
        displaySmall?: React.CSSProperties
        headlineLarge?: React.CSSProperties
        headline?: React.CSSProperties
        headlineSmall?: React.CSSProperties
        titleLarge?: React.CSSProperties
        title?: React.CSSProperties
        titleSmall?: React.CSSProperties
        labelLarge?: React.CSSProperties
        label?: React.CSSProperties
        labelSmall?: React.CSSProperties
        bodyLarge?: React.CSSProperties
        body?: React.CSSProperties
        bodySmall?: React.CSSProperties
        avatar?: React.CSSProperties
    }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        displayLarge: true
        display: true
        displaySmall: true
        headlineLarge: true
        headline: true
        headlineSmall: true
        titleLarge: true
        title: true
        titleSmall: true
        labelLarge: true
        label: true
        labelSmall: true
        bodyLarge: true
        body: true
        bodySmall: true
        avatar: true
    }
}
export const defaultTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 800,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },

    palette: {
        background: {
            default: "#F7F7F9",
        },
        primary: {
            main: "#003936",
            hover: "#006E68",
        },
        secondary: {
            main: "#4AD80E",
            hover: "#47b7b2",
            light: "#E8FFE2",
        },
        grey: {
            100: "#F2F4F8",
            200: "#DDE1E6",
            300: "#C1C7C0",
            400: "#A2A9B0",
            500: "#878D96",
            600: "#697077",
            700: "#4D5358",
            800: "#343A3F",
            900: "#21272A",
        },
        darkgrey: {
            main: "#0f0f0f",
        },
        lightgrey: {
            main: "#F8F9FA",
        },
        info: {
            main: "#161616",
        },
        error: {
            main: "#F54545",
        },
        success: {
            main: "#39C610",
        },
        waiting: {
            main: "#F48D2E",
        },
        disabled: {
            main: "#878D96",
        },
        status: {
            error: "#FFE1E1",
            warning: "#FFEAD6",
            success: "#EBFFE2",
            disabled: "#F2F4F8",
            info: "#F2F4F8",
        },
        settings: {
            main: "#FFF",
            hover: "#F2F4F8",
        },
        backgroundSucces: {
            default: "#E8FFE2",
        },
        selectableChip: {
            bgSelected: "#B5E1B0",
            bgUnselected: "#F2F4F8",
            textSelected: "#003936",
            textUnselected: "#878D96",
            bgUnselectedHover: "#DDE1E6",
        },
        oreveChipButton: {
            bgUnselected: "#FFF",
            bgUnselectedHover: "#DDE1E6",
            bgSelected: "#003936",
            bgSelectedHover: "#006E68",
            textSelected: "#FFF",
            textUnselected: "#003936",
        },
        textfield: {
            default: "#003936",
            disabled: "#878D96",
        },
        selectableButtonGroup: {
            highlight: "#003936",
            highlightHover: "#006E68",
            background: "#F2F4F8",
            textSelected: "#FFF",
            textUnselected: "#003936",
            bgUnselectedHover: "#DDE1E6",
        },
    },
    typography: {
        fontFamily: "Roboto, sans-serif",
        displayLarge: {
            fontSize: "3.56rem",
            fontWeight: 400,
            lineHeight: 1.12,
            letterSpacing: "-0.016rem",
        },
        display: {
            fontSize: "2.8rem",
            fontWeight: 400,
            lineHeight: 1.15,
        },
        displaySmall: {
            fontSize: "2.25rem",
            fontWeight: 400,
            lineHeight: 1.22,
        },
        headlineLarge: {
            fontSize: "2rem",
            fontWeight: 400,
            lineHeight: 1.25,
        },
        headline: {
            fontSize: "1.75rem",
            fontWeight: 400,
            lineHeight: 1.29,
        },
        headlineSmall: {
            fontSize: "1.5rem",
            fontWeight: 400,
            lineHeight: 1.33,
        },
        titleLarge: {
            fontSize: "1.375rem",
            fontWeight: 400,
            lineHeight: 1.27,
        },
        title: {
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: "0.01rem",
        },
        titleSmall: {
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.43,
            letterSpacing: "0.006rem",
        },
        labelLarge: {
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.43,
            letterSpacing: "0.006rem",
        },
        label: {
            fontSize: "0.75rem",
            fontWeight: 500,
            lineHeight: 1.33,
            letterSpacing: "+0.03rem",
        },
        labelSmall: {
            fontSize: "0.688rem",
            fontWeight: 500,
            lineHeight: 1.45,
            letterSpacing: "0.03rem",
        },
        bodyLarge: {
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: "0.03rem",
        },
        body: {
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.43,
            letterSpacing: "0.016rem",
        },
        bodySmall: {
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: 1.33,
            letterSpacing: "0.025rem",
        },
        avatar: {
            fontFamily: "Roboto",
            fontSize: "9.345px",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "12.015px",
            letterSpacing: "0.067px",
        },
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    // Map the new variant to render a <h1> by default
                    displayLarge: "h1",
                    display: "h1",
                    displaySmall: "h1",
                    headlineLarge: "h2",
                    headline: "h2",
                    headlineSmall: "h2",
                    titleLarge: "h3",
                    title: "h3",
                    titleSmall: "h3",
                    labelLarge: "span",
                    label: "span",
                    labelSmall: "span",
                    bodyLarge: "p",
                    body: "p",
                    bodySmall: "p",
                },
                variant: "body",
                color: "darkgrey.main",
            },
        },
    },
})
